import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { doc, onSnapshot, updateDoc, type DocumentSnapshot } from 'firebase/firestore'
import { customerSchema, type Customer } from '@gh/common/models'
import { firestore } from '@/firebase'
import { useAuthStore } from './auth'
import createLogger from '@/utils/logger'
import { STATE_ABBREVIATIONS } from '@/utils/usStates'
import type { ClassId, CustomerId } from '@gh/common/models'
import { waitUntilReady } from '@/utils/storeUtils'

const logger = createLogger('stores/customer.ts')

export const useCustomerStore = defineStore('customer', () => {
  const customer = ref<Customer | null>(null)
  const authStore = useAuthStore()
  // const brandingStore = useBrandingStore()
  const unsubscribeAuth: (() => void) | null = null
  let unsubscribeCustomerSnapshot: (() => void) | null = null

  const authUser = computed(() => authStore.user)
  const isCustomer = computed(() => authStore.isCustomer)
  const isAdminOrCoachOrInstructor = computed(
    () => authStore.isAdmin || authStore.isCoach || authStore.isInstructor,
  )
  const impersonatedCustomerId = ref<CustomerId | null>(null)

  const customerId = computed(() => {
    if (!authUser.value) {
      return null
    }
    if (isAdminOrCoachOrInstructor.value) {
      return impersonatedCustomerId.value
    }
    if (isCustomer.value && impersonatedCustomerId.value) {
      throw new Error('Impersonated customer ID is set but logged in as a customer')
    }
    return authUser.value!.uid
  })

  const isLoading = computed(() => !!authUser.value && !customer.value)

  const signupCompleted = computed(() => !!customer.value?.signupFlowCompleted)

  watch(
    customerId,
    async (newVal) => {
      if (newVal) {
        await retrieveCustomerFromFirestore()
      } else {
        if (unsubscribeCustomerSnapshot) {
          unsubscribeCustomerSnapshot()
        }
      }
    },
    { immediate: true },
  )

  function parseAndSetCustomerData(docSnap: DocumentSnapshot) {
    if (docSnap.exists()) {
      const { success, data, error } = customerSchema.safeParse({
        id: docSnap.id,
        ...docSnap.data(),
      })
      if (!success) {
        logger.error('Error parsing customer data', {
          customerId: docSnap.id,
          zodError: error?.flatten?.() ?? error,
        })
        return
      }
      customer.value = data
    } else {
      // Document doesn't exist, clear customer data
      logger.info('Customer document not found, clearing customer data')
      customer.value = null
    }
  }

  async function retrieveCustomerFromFirestore() {
    if (unsubscribeCustomerSnapshot) {
      unsubscribeCustomerSnapshot()
    }

    // Then subscribe to real-time updates
    unsubscribeCustomerSnapshot = onSnapshot(
      doc(firestore, 'customers', customerId.value!),
      (docSnap) => {
        parseAndSetCustomerData(docSnap)
      },
    )
  }

  const stateOfResidenceAbbreviation = computed(() => {
    if (!customer.value?.stateOfResidence) return ''

    return STATE_ABBREVIATIONS[customer.value.stateOfResidence] || ''
  })

  function untilLoadingIsDone() {
    return waitUntilReady({ continueOnTrue: () => !isLoading.value })
  }

  function recentlyCreated() {
    if (!customer.value) {
      return false
    }
    return (
      customer.value.createdAt &&
      new Date(customer.value.createdAt).getTime() > Date.now() - 1000 * 60 * 60 * 24 * 2
    )
  }

  async function addClassToFavorites(classId: ClassId) {
    if (!customer.value) {
      return
    }
    const favouriteClassesNew = [...(customer.value.favouriteClasses || []), classId]
    await updateDoc(doc(firestore, 'customers', customer.value.id), {
      favouriteClasses: favouriteClassesNew,
      hasEnrolled: true,
    })
  }

  async function removeClassFromFavorites(classId: ClassId) {
    if (!customer.value) {
      return
    }
    const favouriteClassesNew = customer.value.favouriteClasses?.filter((id) => id !== classId)
    await updateDoc(doc(firestore, 'customers', customer.value.id), {
      favouriteClasses: favouriteClassesNew,
    })
  }

  const isEnrolledInClass = (classId: ClassId) => {
    if (!customer.value) {
      return false
    }
    return customer.value.favouriteClasses?.includes(classId) ?? false
  }

  async function enrollInClass(classId: ClassId) {
    if (!customer.value) {
      return
    }

    if (isEnrolledInClass(classId)) {
      return
    }

    await addClassToFavorites(classId)
  }

  async function unenrollFromClass(classId: ClassId) {
    if (!customer.value) {
      return
    }

    if (!isEnrolledInClass(classId)) {
      return
    }

    await removeClassFromFavorites(classId)
  }

  function impersonateCustomer(customerId: CustomerId) {
    if (!isAdminOrCoachOrInstructor.value) {
      throw new Error('Only admins, coaches and instructors can impersonate customers')
    }
    impersonatedCustomerId.value = customerId
  }

  function stopImpersonatingCustomer() {
    if (!isAdminOrCoachOrInstructor.value) {
      throw new Error('Only admins, coaches and instructors can stop impersonating customers')
    }
    impersonatedCustomerId.value = null
  }

  function onUnmounted() {
    if (unsubscribeAuth) {
      unsubscribeAuth()
    }
    if (unsubscribeCustomerSnapshot) {
      unsubscribeCustomerSnapshot()
    }
  }

  function signOut() {
    if (unsubscribeCustomerSnapshot) {
      unsubscribeCustomerSnapshot()
    }
    customer.value = null
  }

  return {
    isLoading,
    customerId,
    customer,
    impersonatedCustomerId,
    signupCompleted,
    onUnmounted,
    retrieveCustomerFromFirestore,
    untilLoadingIsDone,
    stateOfResidenceAbbreviation,
    addClassToFavorites,
    removeClassFromFavorites,
    recentlyCreated,
    signOut,
    isEnrolledInClass,
    enrollInClass,
    unenrollFromClass,
    impersonateCustomer,
    stopImpersonatingCustomer,
  }
})
